import React, { useState, useEffect, useRef } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { RequestType, getPipelineConfig, calculateProgress } from '../utils/pipelineSteps';
import PipelineFlow from './PipelineFlow';

interface PipelineLoadingOverlayProps {
  isVisible: boolean;
  requestType: RequestType;
  query?: string;
  progress?: number; // Optional real progress (0-100)
  estimatedDuration?: number; // Total estimated seconds
}

const PipelineLoadingOverlay: React.FC<PipelineLoadingOverlayProps> = ({
  isVisible,
  requestType,
  query,
  progress: externalProgress,
  estimatedDuration: externalDuration,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  
  const config = getPipelineConfig(requestType);
  const { steps, totalEstimatedDuration } = config;
  const estimatedDuration = externalDuration || totalEstimatedDuration;
  
  // Minimum time to display each step (in milliseconds) - slows down animation
  const MIN_STEP_DISPLAY_TIME = 2000; // 2 seconds per step minimum
  
  useEffect(() => {
    if (!isVisible) {
      // Reset when hidden
      setCurrentStepIndex(0);
      setSimulatedProgress(0);
      startTimeRef.current = null;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    
    // Start simulation when visible
    startTimeRef.current = Date.now();
    
    // Calculate step durations with minimum display time
    const stepDurations = steps.map(step => {
      const baseDuration = (step.estimatedDuration / totalEstimatedDuration) * estimatedDuration * 1000;
      return Math.max(baseDuration, MIN_STEP_DISPLAY_TIME); // Ensure minimum time
    });
    
    const updateProgress = () => {
      if (!startTimeRef.current) return;
      
      const elapsed = Date.now() - startTimeRef.current;
      
      // Calculate which step we should be on based on elapsed time AND minimum display time
      let accumulatedTime = 0;
      let newStepIndex = 0;
      
      for (let i = 0; i < stepDurations.length; i++) {
        const stepDuration = stepDurations[i];
        const stepStartTime = accumulatedTime;
        const stepEndTime = accumulatedTime + stepDuration;
        
        // Check if we've been on this step long enough (minimum display time)
        if (elapsed >= stepEndTime) {
          newStepIndex = i + 1;
        } else if (elapsed >= stepStartTime) {
          // We're currently in this step
          newStepIndex = i;
          break;
        } else {
          // Haven't reached this step yet
          break;
        }
        
        accumulatedTime = stepEndTime;
      }
      
      // Ensure we don't go beyond the last step
      if (newStepIndex >= steps.length) {
        newStepIndex = steps.length - 1;
      }
      
      // Update step index (React will handle deduplication)
      setCurrentStepIndex(newStepIndex);
      
      // Calculate progress based on total adjusted duration
      const totalAdjustedDuration = stepDurations.reduce((sum, dur) => sum + dur, 0);
      const progress = Math.min(95, (elapsed / totalAdjustedDuration) * 100);
      setSimulatedProgress(progress);
      
      // Use external progress if provided
      if (externalProgress !== undefined) {
        const progressBasedStep = Math.floor((externalProgress / 100) * steps.length);
        setCurrentStepIndex(Math.min(progressBasedStep, steps.length - 1));
      }
    };
    
    // Update every 200ms for smoother but slower animation
    intervalRef.current = setInterval(updateProgress, 200);
    updateProgress();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, steps, totalEstimatedDuration, estimatedDuration, externalProgress]);
  
  if (!isVisible) return null;
  
  const currentStep = steps[currentStepIndex] || steps[steps.length - 1];
  const progress = externalProgress !== undefined ? externalProgress : simulatedProgress;
  
  const getRequestTypeLabel = () => {
    switch (requestType) {
      case 'rag_query':
        return 'Analyzing Query';
      case 'qbr_generation':
        return 'Generating QBR Report';
      case 'briefing_generation':
        return 'Generating Briefing';
      case 'ui_navigation':
        return 'Navigating';
      default:
        return 'Processing';
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border p-8 max-w-7xl mx-4 w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-2">
            {getRequestTypeLabel()}
          </h3>
          {query && (
            <p className="text-sm text-muted-foreground mt-2">
              "{query}"
            </p>
          )}
        </div>
        
        {/* Pipeline Flow */}
        <div className="mb-8">
          <PipelineFlow 
            steps={steps} 
            currentStepIndex={currentStepIndex}
          />
        </div>
        
        {/* Current Step Description */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center space-x-3 px-4 py-2 bg-muted/50 rounded-lg">
            <div className={`w-2 h-2 rounded-full ${
              currentStepIndex < steps.length 
                ? 'bg-primary animate-pulse' 
                : 'bg-green-500'
            }`} />
            <p className="text-sm font-medium text-foreground">
              {currentStep.description}
            </p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-300 ease-out relative"
              style={{ width: `${Math.min(progress, 100)}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span>Step {currentStepIndex + 1} of {steps.length}</span>
            <span className="font-semibold text-primary">{Math.round(progress)}%</span>
            <span>{Math.round(estimatedDuration - (progress / 100) * estimatedDuration)}s remaining</span>
          </div>
        </div>
        
        {/* Steps Timeline */}
        <div className="mt-6 space-y-2 max-h-40 overflow-y-auto">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex items-center space-x-3 text-sm transition-all duration-300 ${
                index < currentStepIndex
                  ? 'text-green-600 dark:text-green-400'
                  : index === currentStepIndex
                  ? 'text-primary font-medium'
                  : 'text-muted-foreground'
              }`}
            >
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                index < currentStepIndex
                  ? 'bg-green-500'
                  : index === currentStepIndex
                  ? 'bg-primary animate-pulse'
                  : 'bg-muted'
              }`}>
                {index < currentStepIndex ? (
                  <CheckCircleIcon className="w-3 h-3 text-white" />
                ) : (
                  <span className="text-xs font-semibold text-white">{index + 1}</span>
                )}
              </div>
              <span>{step.label}</span>
              {index === currentStepIndex && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {currentStep.description}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PipelineLoadingOverlay;

